/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the software.

*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";

// Vision UI Dashboard React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Vision UI Dashboard React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Vision UI Dashboard React routes
import routes from "routes";

// Vision UI Dashboard React contexts
import { useVisionUIController, setMiniSidenav, setOpenConfigurator } from "context";
import PrivateRoute from "./context/PrivateRoute";

import { useAuth } from "./context/AuthContext";
import SubscriptionNotice from "components/SubscriptionNotice";
import { logger } from "./services/logger";
import DossierDetail from "layouts/dossier";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import ResetPassword from "layouts/authentication/reset-password";
import ForgotPassword from "layouts/authentication/forgot-password";
import Dashboard from "layouts/dashboard";
import Leads from "layouts/leads";
import Profile from "layouts/configuracao";
import Subscribe from "layouts/subscribe";
import DeployControl from "layouts/deploy";


export default function App() {
  const { user, profile } = useAuth();
  logger.debug("App.js: Renderizando. Objeto de usuário atual:", user);
  logger.info(`App.js: Verificando status da assinatura. Perfil atual:`, profile);

  // A lógica de bloqueio: mostra o aviso se o perfil do usuário existe e o status não é 'active'.
  const showSubscriptionNotice = profile && profile.subscription_status !== 'active';

  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const allowedDeployHosts = ["gs.saferdock.com", "localhost", "localhost:3000"];
  const isDeployHost =
    typeof window !== "undefined" ? allowedDeployHosts.includes(window.location.host) : false;

  // Debug helpers for runtime errors in production.
  useEffect(() => {
    const handleError = (event) => {
      logger.error("Global window error capturado", {
        message: event?.message,
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
        stack: event?.error?.stack,
        route: window.location?.pathname,
      });
    };

    const handleRejection = (event) => {
      logger.error("Promise rejeitada sem tratamento", {
        reason: event?.reason,
        stack: event?.reason?.stack,
        route: window.location?.pathname,
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  useEffect(() => {
    logger.info("Theme snapshot carregado", {
      hasPalette: Boolean(theme?.palette),
      hasTypography: Boolean(theme?.typography),
      hasFunctions: Boolean(theme?.functions),
      hasBorders: Boolean(theme?.borders),
      hasBoxShadows: Boolean(theme?.boxShadows),
      paletteKeys: Object.keys(theme?.palette || {}),
      typographyKeys: Object.keys(theme?.typography || {}),
      functionKeys: Object.keys(theme?.functions || {}),
    });
  }, []);

  useEffect(() => {
    logger.debug("Vision UI controller atualizado", {
      miniSidenav,
      direction,
      layout,
      openConfigurator,
      sidenavColor,
    });
  }, [miniSidenav, direction, layout, openConfigurator, sidenavColor]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const whatsappButton = (
    <VuiBox
      component="a"
      href="https://wa.me/5511989205971?text=Oi%20Roberto!%20Preciso%20de%20ajuda%20com%20o%20SaferDock."
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Conversar no WhatsApp"
      sx={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '60px',
        height: '60px',
        backgroundColor: '#25D366',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        textDecoration: 'none',
        boxShadow: '0 2px 12px 0 rgba(0,0,0,0.16)',
        cursor: 'pointer'
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" 
           viewBox="0 0 32 32" 
           aria-hidden="true"
           style={{ width: '32px', height: '32px' }}
      >
        <path fill="#ffffff" d="M16 .396C7.16.396 0 7.555 0 16.396c0 2.87.75 5.59 2.18 8.012L.1 31.396l7.17-1.97a16.324 16.324 0 0 0 8.73 2.467c8.84 0 16-7.16 16-16 0-8.84-7.16-15.997-16-15.997zm0 29.201a13.15 13.15 0 0 1-7.064-2.02l-.508-.316-4.252 1.168 1.135-4.144-.33-.538A13.13 13.13 0 0 1 2.8 16.396c0-7.4 6.002-13.4 13.2-13.4s13.2 6.002 13.2 13.4-6.002 13.2-13.2 13.2zm7.584-9.755c-.414-.207-2.454-1.21-2.834-1.348-.38-.14-.658-.207-.935.208s-1.07 1.347-1.31 1.623c-.24.275-.48.31-.894.104s-1.74-.64-3.318-2.04c-1.226-1.093-2.05-2.444-2.29-2.86s-.025-.637.182-.844c.187-.186.413-.48.62-.72.207-.24.275-.413.413-.688.14-.275.07-.517-.035-.72-.104-.207-.935-2.26-1.28-3.1-.337-.81-.68-.702-.935-.714l-.794-.01a1.54 1.54 0 0 0-1.115.52c-.38.413-1.49 1.453-1.49 3.545s1.526 4.114 1.738 4.4c.207.275 3.006 4.59 7.282 6.44 1.018.44 1.81.703 2.43.898 1.02.323 1.946.278 2.68.17.818-.122 2.454-1.002 2.8-1.97.344-.968.344-1.797.24-1.97-.104-.172-.38-.275-.794-.48z"/>
      </svg>
    </VuiBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand=""
              brandName="Safer Dock"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            {whatsappButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
            <Route path="/authentication/sign-in" element={<SignIn />} />
            <Route path="/authentication/sign-up" element={<SignUp />} />
            <Route path="/authentication/reset-password/:token" element={<ResetPassword />} />
            <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/leads" element={<Leads />} />
                <Route path="/configuracao" element={<Profile />} />
                <Route path="/dossier/by-fingerprint/:fingerprint" element={<DossierDetail />} />
                {isDeployHost && <Route path="/deploy" element={<DeployControl />} />}
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand=""
              brandName="Safer Dock"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            {whatsappButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
            <Route path="/authentication/sign-in" element={<SignIn />} />
            <Route path="/authentication/sign-up" element={<SignUp />} />
            <Route path="/authentication/reset-password/:token" element={<ResetPassword />} />
            <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/leads" element={<Leads />} />
                <Route path="/configuracao" element={<Profile />} />
                <Route path="/dossier/by-fingerprint/:fingerprint" element={<DossierDetail />} />
                {isDeployHost && <Route path="/deploy" element={<DeployControl />} />}
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
        {showSubscriptionNotice && <SubscriptionNotice />}
    </ThemeProvider>
  );
}

/*!
Toda glória e honra a Deus. Amém!!
*/
